import { RefObject, FocusEvent, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  analytics,
  SearchSuggestion,
  useKeyNavigation,
} from '@silvertours/front-entities';
import { Theme } from '@silvertours/front-shared';
import {
  StyledList,
  StyledButton,
  ErrorMessage,
  ListItem,
} from './LocationSuggest.styles';
import { SearchResultItem } from './SearchResultItem';
import { useSearchContext } from '../context';
import { useStageContext } from '../../StageLegacy/context';
import { RentalDirection } from '../context/types';

type LocationSuggestProps = {
  connectedInputContainer?: RefObject<HTMLInputElement | null>;
  loading: boolean;
  results: Array<SearchSuggestion> | null;
  onLastElementBlur: (event?: FocusEvent<HTMLElement, Element>) => void;
  onItemClick: (suggestion?: SearchSuggestion) => void;
  onItemSelect: (id?: string) => void;
  direction: RentalDirection;
};

const LocationSuggest = ({
  connectedInputContainer,
  loading,
  results,
  onLastElementBlur,
  onItemClick,
  onItemSelect,
  direction,
}: LocationSuggestProps) => {
  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const { state } = useSearchContext();
  const { setStageImage } = useStageContext();
  const listRef = useRef<HTMLUListElement>(null);

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  const handleEnter = (item?: SearchSuggestion) => {
    if (item) {
      analytics.generic.trackLocationSuggestionClick(
        direction,
        item,
        results?.indexOf(item) || 0,
      );
    }
    onItemClick(item);
  };

  const handleEscape = () => {
    onLastElementBlur();
  };

  const { focusedItem, onMouseEnter, onMouseLeave } =
    useKeyNavigation<SearchSuggestion>(
      results || [],
      listRef,
      handleEnter,
      handleEscape,
    );

  useEffect(() => {
    onItemSelect(
      focusedItem === null
        ? undefined
        : `locationSuggestItem-${focusedItem + 1}`,
    );
  }, [focusedItem, onItemSelect]);

  useEffect(() => {
    if (isMobileTouchDevice && connectedInputContainer?.current) {
      connectedInputContainer.current.scrollIntoView();
    }
  }, [isMobileTouchDevice, connectedInputContainer]);

  const handleClick = (suggestion: SearchSuggestion) => {
    onItemClick(suggestion);
    setStageImage(suggestion.image_path);
  };

  if (!loading && !results?.length && !state?.depLocation) {
    return (
      <ErrorMessage data-testid="locationSuggestError">
        {t(direction === 'departure' ? 'errorTexts.dep' : 'errorTexts.dest')}
      </ErrorMessage>
    );
  }

  // avoid to show empty list with a visible border
  if (!results?.length) {
    return null;
  }

  return (
    results && (
      <StyledList
        ref={listRef}
        id="locationSuggest"
        role="grid"
        aria-label={t('results')}
      >
        {results.map((suggestion: SearchSuggestion, index) => (
          <ListItem
            key={suggestion.objectID}
            role="presentation"
            id={suggestion.id}
          >
            <StyledButton
              type="button"
              tabIndex={0}
              onBlur={onLastElementBlur}
              onMouseDown={() => {
                analytics.generic.trackLocationSuggestionClick(
                  direction,
                  suggestion,
                  index,
                );
                handleClick(suggestion);
              }}
              onMouseEnter={() => onMouseEnter(index)}
              onMouseLeave={onMouseLeave}
              id={`locationSuggestItem-${index + 1}`}
              role="row"
              aria-label={`${suggestion.cityName} ${suggestion.typeDisplay}`}
              aria-selected={index === focusedItem}
            >
              <SearchResultItem suggestion={suggestion} />
            </StyledButton>
          </ListItem>
        ))}
      </StyledList>
    )
  );
};

export { LocationSuggest };
